import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { createActionResponseFactory } from '@core/ngrx-legacy';
import { generateUuid } from '@core/utils-legacy';
import {
  getTellentNoteScope,
  TellentMentionableActions,
  TellentMentionableSelectors,
  type TellentNoteCreatePayload,
  TellentNotesActions,
  TellentNotesSelectors,
} from '@tellent-ask-hr/notes-data';
import type { TellentNote } from '@tellent-ask-hr/notes-types';

@Injectable({ providedIn: 'root' })
export class TellentNotesService {
  private readonly store = inject(Store);
  private readonly notesActionResponse = createActionResponseFactory(TellentNotesActions);

  public loadNotes(resourceType: TellentNote['resourceType'], resourceId: number) {
    const scope = getTellentNoteScope(resourceType, resourceId);
    return this.notesActionResponse(
      TellentNotesActions.fetchCollection,
      { payload: { resourceType, resourceId } },
      {
        success: TellentNotesActions.fetchCollectionSuccess,
        failed: TellentNotesActions.fetchCollectionFailed,
        successResponse: ({ store }) => store.select(TellentNotesSelectors.entities({ scope })),
        failedResponse: ({ store }) => store.select(TellentNotesSelectors.error({ scope })),
      },
    );
  }

  public getNotes(resourceType: TellentNote['resourceType'], resourceId: number) {
    const scope = getTellentNoteScope(resourceType, resourceId);
    return this.store.select(TellentNotesSelectors.entities({ scope }));
  }

  public createNote(
    resourceType: TellentNote['resourceType'],
    resourceId: number,
    note: TellentNoteCreatePayload,
  ) {
    return this.notesActionResponse(
      TellentNotesActions.createOnePessimistic,
      {
        payload: { resourceType, resourceId, note, correlationId: generateUuid() },
      },
      {
        success: TellentNotesActions.createOnePessimisticSuccess,
        failed: TellentNotesActions.createOnePessimisticFailed,
      },
    );
  }

  public updateNote(id: number, note: TellentNoteCreatePayload) {
    return this.notesActionResponse(
      TellentNotesActions.updateOnePessimistic,
      { payload: { id, note } },
      {
        success: TellentNotesActions.updateOnePessimisticSuccess,
        failed: TellentNotesActions.updateOnePessimisticFailed,
      },
    );
  }

  public deleteNote(id: number) {
    this.store.dispatch(
      TellentNotesActions.removeOnePessimistic({
        payload: { id },
      }),
    );
  }

  public fetchMentionables(resourceType: TellentNote['resourceType'], resourceId: number) {
    this.store.dispatch(
      TellentMentionableActions.fetchCollection({
        payload: { resourceType, resourceId },
      }),
    );
  }

  public getMentionables(resourceType: TellentNote['resourceType'], resourceId: number) {
    const scope = getTellentNoteScope(resourceType, resourceId);
    return this.store.select(TellentMentionableSelectors.entities({ scope }));
  }
}
